/**
 * Tab Display Name Mapping
 * Maps tab names to their display names based on user role
 */

import { UserRole, HOTEL_ROLES } from '../constants/roles';

interface TabDisplayMapping {
  [tabName: string]: {
    [role: string]: string;
  } | string;
}

/**
 * Maps tab names to role-specific display names
 * If a string is provided instead of an object, that display name is used for all roles
 */
const tabDisplayMapping: TabDisplayMapping = {
  // HOME tab (for super admin) shows as "Home"
  'HOME': 'Home',
  
  // HOTELS tab shows as "Home" for hotel roles, "Hotels" for ALO_SUPER
  // Brand admins have this permission but shouldn't see "Home" (they have Brand Dashboard instead)
  'HOTELS': {
    // Dynamically map all hotel roles to 'Home'
    ...Object.fromEntries(HOTEL_ROLES.map(role => [role, 'Home'])),
    'ALO_SUPER': 'Hotels',
    'BRAND_ADMIN': '', // Empty string = don't show in sidebar
  },
  
  // Brand-specific tabs
  'BRAND DASHBOARD': 'Brand Dashboard',
  'BRAND HOTELS': 'Brand Hotels',
  
  // All other tabs use their original names
  'BOOST YOUR BUSINESS': 'Boost Your Business',
  'IMPORT': 'Import',
  'OUR APPROACH': 'Our Approach',
  'ABOUT ALŌ INDEX': 'About Alō Index',
  'SAMPLE QUESTIONNAIRE': 'Sample Questionnaire',
  'TERMS OF USE': 'Terms of Use',
};

/**
 * Get the display name for a tab based on user role
 * @param tabName - The tab name from the API (e.g., "HOTELS", "HOME")
 * @param userRole - The user's role (e.g., "HOTEL_ADMIN", "ALO_SUPER")
 * @returns The display name for the tab
 */
export const getTabDisplayName = (tabName: string, userRole?: UserRole): string => {
  const mapping = tabDisplayMapping[tabName.toUpperCase()];
  
  if (!mapping) {
    // If no mapping exists, return the original tab name with proper casing
    return tabName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  // If mapping is a string, return it directly
  if (typeof mapping === 'string') {
    return mapping;
  }
  
  // If mapping is an object, get role-specific display name
  // Check for undefined explicitly to allow empty strings (used to hide tabs)
  if (userRole && mapping[userRole] !== undefined) {
    return mapping[userRole];
  }
  
  // Default: return the first value in the mapping object or the original name
  const defaultValue = Object.values(mapping)[0];
  return typeof defaultValue === 'string' ? defaultValue : tabName;
};

/**
 * Get display names for multiple tabs based on user role
 * @param tabs - Array of tab names from the API
 * @param userRole - The user's role
 * @returns Array of objects with original tab name and display name
 */
export const getTabsWithDisplayNames = (
  tabs: string[],
  userRole?: UserRole
): Array<{ tabName: string; displayName: string }> => {
  return tabs.map(tabName => ({
    tabName: tabName.toUpperCase(),
    displayName: getTabDisplayName(tabName, userRole),
  }));
};

/**
 * Check if a navigation item should be shown based on available tabs
 * @param navItemText - The text of the navigation item (e.g., "Home", "Hotels")
 * @param userTabs - Array of tab names the user has access to
 * @param userRole - The user's role
 * @returns true if the navigation item should be shown
 */
export const shouldShowNavItem = (
  navItemText: string,
  userTabs: string[],
  userRole?: UserRole
): boolean => {
  // Get display names for all user tabs
  const tabsWithDisplay = getTabsWithDisplayNames(userTabs, userRole);
  
  // Check if any tab's display name matches the nav item text
  // Filter out empty display names (used to hide certain tabs for specific roles)
  return tabsWithDisplay.some(
    tab => tab.displayName && tab.displayName.toUpperCase() === navItemText.toUpperCase()
  );
};
